import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';

export type AvailabilityRange = {
  id: string;
  type: 'booked' | 'hold' | 'second-hold';
  start: string;
  end: string;
  project?: string;
};

export function useAvailability() {
  const { user } = useAuth();
  const [ranges, setRanges] = useState<AvailabilityRange[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setRanges([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('availability_ranges')
      .select('*')
      .eq('user_id', user.id)
      .order('start_date', { ascending: true });
    if (!error && data) {
      setRanges((data as Record<string, unknown>[]).map(row => ({
        id: row.id as string,
        type: row.type as 'booked' | 'hold' | 'second-hold',
        start: row.start_date as string,
        end: row.end_date as string,
        project: (row.project_name as string) || undefined,
      })));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addRange = async (range: Omit<AvailabilityRange, 'id'>): Promise<void> => {
    if (!user) return;
    const { data, error } = await supabase.from('availability_ranges').insert({
      user_id: user.id,
      type: range.type,
      start_date: range.start,
      end_date: range.end,
      project_name: range.project || null,
    }).select().single();
    if (!error && data) {
      const row = data as Record<string, unknown>;
      setRanges(prev => [...prev, {
        id: row.id as string,
        type: row.type as 'booked' | 'hold' | 'second-hold',
        start: row.start_date as string,
        end: row.end_date as string,
        project: (row.project_name as string) || undefined,
      }]);
    }
  };

  const updateRange = async (id: string, updates: Omit<AvailabilityRange, 'id'>): Promise<void> => {
    if (!user) return;
    const { data, error } = await supabase.from('availability_ranges').update({
      type: updates.type,
      start_date: updates.start,
      end_date: updates.end,
      project_name: updates.project || null,
    }).eq('id', id).eq('user_id', user.id).select().single();
    if (!error && data) {
      const row = data as Record<string, unknown>;
      setRanges(prev => prev.map(r => r.id === id ? {
        id: row.id as string,
        type: row.type as 'booked' | 'hold' | 'second-hold',
        start: row.start_date as string,
        end: row.end_date as string,
        project: (row.project_name as string) || undefined,
      } : r));
    }
  };

  const deleteRange = async (id: string): Promise<void> => {
    if (!user) return;
    const { error } = await supabase.from('availability_ranges').delete().eq('id', id).eq('user_id', user.id);
    if (!error) setRanges(prev => prev.filter(r => r.id !== id));
  };

  return { ranges, loading, addRange, updateRange, deleteRange, refresh };
}
